import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, Minus, Plus, ArrowRight, ShoppingCart, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getProductImage = (product) => {
  if (product?.images?.length > 0) return product.images[0];
  return product?.image_url || '';
};

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal, getCartCount } = useCart();
  const { isAuthenticated } = useAuth();
  const { formatLocalPrice } = useCurrency();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (cart.items && cart.items.length > 0) {
      fetchProductDetails();
    } else {
      setLoading(false);
    }
  }, [cart]);

  const fetchProductDetails = async () => {
    try {
      const productIds = cart.items.map(item => item.product_id);
      const productPromises = productIds.map(id => axios.get(`${API}/products/${id}`));
      const responses = await Promise.all(productPromises);
      
      const productsMap = {};
      responses.forEach(response => {
        productsMap[response.data.id] = response.data;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Failed to fetch product details', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (item, delta) => {
    const newQuantity = item.quantity + delta;
    if (newQuantity > 0 && newQuantity <= 10) {
      updateQuantity(item.product_id, item.variant_id, newQuantity);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-health-emerald border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-background font-body">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[70vh] px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-24 h-24 bg-health-mint rounded-3xl flex items-center justify-center mx-auto mb-8">
              <ShoppingCart className="w-12 h-12 text-health-emerald" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-health-ink mb-4">Your cart is empty</h1>
            <p className="text-slate-500 mb-8 max-w-md">
              Your medicine cabinet will thank you. Browse pain & cold relief, first aid and home health devices.
            </p>
            <Link to="/products">
              <Button className="bg-health-emerald hover:bg-health-emerald-dark text-white rounded-full font-bold px-8 py-6" data-testid="cart-empty-shop-button">
                Start Shopping
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-body">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-health-emerald">
            Your bag
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-health-ink mt-4">
            Shopping bag
            <span className="text-slate-400 text-2xl ml-4">({getCartCount()} items)</span>
          </h1>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => {
              const product = products[item.product_id] || {};
              return (
                <motion.div
                  key={`${item.product_id}-${item.variant_id || 'default'}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-3xl border border-health-line shadow-soft p-4 md:p-5 group hover:shadow-lift transition-shadow"
                >
                  <div className="flex gap-4 md:gap-6">
                    {/* Image */}
                    <Link 
                      to={`/products/${item.product_id}`}
                      className="flex-shrink-0 w-24 h-24 md:w-32 md:h-32 bg-health-mint rounded-2xl overflow-hidden"
                    >
                      <img
                        src={item.image || getProductImage(product)}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </Link>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/products/${item.product_id}`}
                        className="block"
                      >
                        <h3 className="font-heading text-lg text-health-ink font-semibold line-clamp-2 hover:text-health-emerald transition-colors">
                          {item.name}
                        </h3>
                      </Link>
                      
                      {(item.color || item.size) && (
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          {item.color && (
                            <span className="flex items-center gap-1">
                              <span 
                                className="w-3 h-3 rounded-full border border-slate-300" 
                                style={{ backgroundColor: item.color }}
                              ></span>
                              {item.color}
                            </span>
                          )}
                          {item.size && <span>Size: {item.size}</span>}
                        </div>
                      )}

                      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-1 bg-health-mint rounded-full p-1">
                          <button
                            onClick={() => handleQuantityChange(item, -1)}
                            disabled={item.quantity <= 1}
                            className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-slate-600 hover:text-health-emerald disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-12 text-center text-health-ink font-medium">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(item, 1)}
                            disabled={item.quantity >= 10}
                            className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-slate-600 hover:text-health-emerald disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Price & Remove */}
                        <div className="flex items-center gap-6">
                          <span className="font-heading text-xl font-bold text-health-ink">
                            {formatLocalPrice(item.price * item.quantity)}
                          </span>
                          <button
                            onClick={() => removeFromCart(item.product_id, item.variant_id)}
                            className="p-2 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                            data-testid={`remove-item-${item.product_id}`}
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-[2rem] border border-health-line shadow-soft p-6 sticky top-28"
            >
              <h2 className="font-heading text-xl font-bold text-health-ink mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal ({getCartCount()} items)</span>
                  <span className="text-health-ink">{formatLocalPrice(getCartTotal())}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Shipping</span>
                  <span className="text-health-emerald">Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-health-line pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="font-heading text-lg font-bold text-health-ink">Total</span>
                  <span className="font-heading text-2xl font-bold text-health-ink" data-testid="cart-total">
                    {formatLocalPrice(getCartTotal())}
                  </span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full bg-health-emerald hover:bg-health-emerald-dark text-white rounded-full font-heading font-bold py-7 text-lg shadow-lift transition-colors"
                data-testid="proceed-to-checkout"
              >
                <ShoppingBag className="mr-2 h-5 w-5" />
                Proceed to checkout
              </Button>

              <Link to="/products" className="block mt-4">
                <Button
                  variant="outline"
                  className="w-full rounded-full py-6 border-2 border-health-line text-slate-700 hover:text-health-emerald hover:border-health-emerald font-bold"
                  data-testid="cart-continue-shopping"
                >
                  Continue shopping
                </Button>
              </Link>

              {/* Trust Badges */}
              <div className="mt-8 pt-6 border-t border-health-line">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="text-slate-500 text-xs">
                    <div className="w-10 h-10 rounded-xl bg-health-mint flex items-center justify-center mx-auto mb-2">
                      <ShoppingBag className="h-4 w-4 text-health-emerald" />
                    </div>
                    Secure Checkout
                  </div>
                  <div className="text-slate-500 text-xs">
                    <div className="w-10 h-10 rounded-xl bg-health-mint flex items-center justify-center mx-auto mb-2">
                      <Zap className="h-4 w-4 text-health-coral" />
                    </div>
                    Discreet packaging
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
